/**
 * Notifications ephemeres (« toasts ») : confirmation d'une action, erreur
 * passagere. La liste est une region `aria-live` presente des le montage : un
 * lecteur d'ecran n'annonce que ce qui s'ajoute a une region deja presente.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { CircleAlert, CircleCheck, Info, X } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { BoutonIcone } from './boutons';

export type TonToast = 'succes' | 'erreur' | 'info';

/** Une erreur se lit plus longtemps qu'une confirmation. */
export const DUREES_TOAST: Readonly<Record<TonToast, number>> = {
  succes: 4000,
  info: 5000,
  erreur: 8000,
};

/** Au-dela, le plus ancien cede sa place : la pile ne couvre jamais l'ecran. */
const NB_MAX_TOASTS = 4;

export interface ApiToasts {
  succes: (message: string, titre?: string) => void;
  erreur: (message: string, titre?: string) => void;
  info: (message: string, titre?: string) => void;
}

interface Toast {
  id: number;
  ton: TonToast;
  message: string;
  titre?: string;
}

const ContexteToasts = createContext<ApiToasts | null>(null);

const APPARENCES: Record<TonToast, { icone: LucideIcon; couleur: string; prefixe: string }> = {
  succes: { icone: CircleCheck, couleur: 'text-succes-600', prefixe: 'Succès' },
  erreur: { icone: CircleAlert, couleur: 'text-danger-600', prefixe: 'Erreur' },
  info: { icone: Info, couleur: 'text-info-600', prefixe: 'Information' },
};

function ElementToast({ toast, onFermer }: { toast: Toast; onFermer: (id: number) => void }) {
  const [enPause, setEnPause] = useState(false);

  // Survol ou focus suspendent la fermeture : on ne retire pas un message en cours de lecture.
  useEffect(() => {
    if (enPause) return undefined;
    const minuteur = window.setTimeout(() => onFermer(toast.id), DUREES_TOAST[toast.ton]);
    return () => window.clearTimeout(minuteur);
  }, [enPause, toast.id, toast.ton, onFermer]);

  const { icone: Icone, couleur, prefixe } = APPARENCES[toast.ton];
  return (
    <li
      className="pointer-events-auto flex w-full max-w-sm animate-apparition items-start gap-3 rounded-lg border border-bordure bg-surface p-3.5 shadow-flottant"
      onMouseEnter={() => setEnPause(true)}
      onMouseLeave={() => setEnPause(false)}
      onFocus={() => setEnPause(true)}
      onBlur={() => setEnPause(false)}
    >
      <Icone aria-hidden="true" className={`mt-0.5 shrink-0 ${couleur}`} />
      <div className="min-w-0 flex-1">
        <span className="sr-only">{prefixe} : </span>
        {toast.titre && <p className="text-sm font-semibold text-encre">{toast.titre}</p>}
        <p className="text-sm text-encre-douce">{toast.message}</p>
      </div>
      <BoutonIcone
        libelle="Fermer la notification"
        icone={X}
        taille="xs"
        onClick={() => onFermer(toast.id)}
        className="-mt-1 -mr-1.5 shrink-0"
      />
    </li>
  );
}

export function FournisseurToasts({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const refCompteur = useRef(0);

  const fermer = useCallback((id: number) => {
    setToasts((liste) => liste.filter((toast) => toast.id !== id));
  }, []);

  const api = useMemo<ApiToasts>(() => {
    const ajouter =
      (ton: TonToast) =>
      (message: string, titre?: string): void => {
        refCompteur.current += 1;
        const toast: Toast = { id: refCompteur.current, ton, message, titre };
        setToasts((liste) => [...liste, toast].slice(-NB_MAX_TOASTS));
      };
    return { succes: ajouter('succes'), erreur: ajouter('erreur'), info: ajouter('info') };
  }, []);

  return (
    <ContexteToasts.Provider value={api}>
      {children}
      {createPortal(
        <ol
          aria-live="polite"
          aria-label="Notifications"
          className="pointer-events-none fixed inset-x-4 bottom-4 z-60 flex flex-col items-end gap-2 sm:inset-x-auto sm:right-6 sm:bottom-6"
        >
          {toasts.map((toast) => (
            <ElementToast key={toast.id} toast={toast} onFermer={fermer} />
          ))}
        </ol>,
        document.body,
      )}
    </ContexteToasts.Provider>
  );
}

export function useToast(): ApiToasts {
  const api = useContext(ContexteToasts);
  if (!api) throw new Error('useToast doit etre utilise dans un FournisseurToasts');
  return api;
}
